import { Component } from '@theme/component';
import { OverlayOpenedEvent, eventBus } from '@theme/events';

const CM_PER_INCH = 2.54;
const MOBILE_BREAKPOINT = 768;

/**
 * PDP size guide — in-place panel host.
 *
 * - open()/close() swap the panel (`ref="panel"`) via `hidden`, with no
 *   animation, replicating the original aeyde behavior. On mobile the panel is
 *   position:fixed below the header: the real header bottom is measured at
 *   open time into `--size-guide-top`, and page scroll is locked via the
 *   `.size-guide-lock` class on <html> and <body> (mobile only, like
 *   production).
 * - Emits OverlayOpenedEvent on open so analytics tracks `size_guide_opened`
 *   (via data-analytics-surface) and other overlays close themselves.
 * - Handles CM/IN unit toggling for the size guide tables: within the
 *   [data-size-guide] scope of the changed select, cells with both data-cm and
 *   data-in swap to the precomputed value; cells with only data-cm convert
 *   arithmetically (cm / 2.54, one decimal).
 * - ESC closes the panel.
 *
 * @extends {Component<{ panel: HTMLElement }>}
 */
class PdpSizeGuide extends Component {
	requiredRefs = ['panel'];

	#isOpen = false;

	/** @type {(() => void)|null} rAF-throttled height updater while open (desktop) */
	#updatePanelHeight = null;

	connectedCallback() {
		super.connectedCallback();
		this.addEventListener('change', this.#handleUnitChange);
		document.addEventListener('keydown', this.#handleKeydown);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.removeEventListener('change', this.#handleUnitChange);
		document.removeEventListener('keydown', this.#handleKeydown);
		this.#stopViewportSizing();
		this.#unlockScroll();
	}

	open() {
		const { panel } = this.refs;
		if (!panel || this.#isOpen) return;

		if (window.innerWidth < MOBILE_BREAKPOINT) {
			const header = document.querySelector('header.header');
			const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 52;
			panel.style.setProperty('--size-guide-top', `${top}px`);
			document.documentElement.classList.add('size-guide-lock');
			document.body.classList.add('size-guide-lock');
		}

		panel.hidden = false;
		this.#isOpen = true;
		this.querySelector('.size-guide__trigger')?.setAttribute('aria-expanded', 'true');
		this.#startViewportSizing();

		eventBus.emit(new OverlayOpenedEvent({ source: this }));
	}

	close() {
		const { panel } = this.refs;
		if (!panel || !this.#isOpen) return;

		panel.hidden = true;
		this.#isOpen = false;
		this.querySelector('.size-guide__trigger')?.setAttribute('aria-expanded', 'false');
		this.#stopViewportSizing();
		this.#unlockScroll();
	}

	/**
	 * Desktop: pin the panel's bottom edge to the viewport bottom by sizing it
	 * to the visible slice (viewport height minus the panel's on-screen top).
	 * That makes the panel a real scroll container, so the wheel scrolls the
	 * size guide content while the cursor is over it and the page elsewhere.
	 * The top edge moves until the sticky column engages, hence the scroll and
	 * resize listeners (rAF-throttled). Mobile keeps its fixed-position CSS.
	 */
	#startViewportSizing() {
		const { panel } = this.refs;
		if (!panel) return;

		let scheduled = false;
		this.#updatePanelHeight = () => {
			if (scheduled) return;
			scheduled = true;
			requestAnimationFrame(() => {
				scheduled = false;
				if (window.innerWidth < MOBILE_BREAKPOINT) {
					panel.style.height = '';
					return;
				}
				const top = Math.max(0, panel.getBoundingClientRect().top);
				panel.style.height = `${Math.max(200, window.innerHeight - top)}px`;
			});
		};

		this.#updatePanelHeight();
		window.addEventListener('scroll', this.#updatePanelHeight, { passive: true });
		window.addEventListener('resize', this.#updatePanelHeight);
	}

	#stopViewportSizing() {
		if (!this.#updatePanelHeight) return;
		window.removeEventListener('scroll', this.#updatePanelHeight);
		window.removeEventListener('resize', this.#updatePanelHeight);
		this.#updatePanelHeight = null;
		this.refs.panel?.style.removeProperty('height');
	}

	#unlockScroll() {
		document.documentElement.classList.remove('size-guide-lock');
		document.body.classList.remove('size-guide-lock');
	}

	/** @param {KeyboardEvent} event */
	#handleKeydown = (event) => {
		if (event.key === 'Escape' && this.#isOpen) this.close();
	};

	/** @param {Event} event */
	#handleUnitChange = (event) => {
		const select = /** @type {HTMLSelectElement|null} */ (
			/** @type {Element} */ (event.target).closest?.('select[data-unit-toggle]')
		);
		if (!select) return;

		const scope = select.closest('[data-size-guide]') ?? this;
		const useInches = select.value === 'IN';

		for (const cell of scope.querySelectorAll('[data-cm][data-in]')) {
			cell.textContent = useInches ? cell.dataset.in : cell.dataset.cm;
		}

		for (const cell of scope.querySelectorAll('[data-cm]:not([data-in])')) {
			const cm = Number(cell.dataset.cm);
			if (!Number.isFinite(cm)) continue;
			cell.textContent = useInches ? `${Math.round((cm / CM_PER_INCH) * 10) / 10}in` : `${cm}cm`;
		}
	};
}

if (!customElements.get('pdp-size-guide')) {
	customElements.define('pdp-size-guide', PdpSizeGuide);
}
